import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  resolveExerciseModality,
  formatExercisePrescription,
  normalizeDiscipline,
  getDiscipline,
} from '@/lib/trainingDisciplines';

const COLORS = {
  primary: [37, 99, 235],
  primaryDark: [29, 78, 216],
  dark: [15, 23, 42],
  muted: [100, 116, 139],
  mutedLight: [148, 163, 184],
  border: [226, 232, 240],
  surface: [248, 250, 252],
  white: [255, 255, 255],
  green: [22, 163, 74],
  greenLight: [220, 252, 231],
  orange: [234, 88, 12],
  orangeLight: [255, 237, 213],
  purple: [124, 58, 237],
  purpleLight: [237, 233, 254],
  red: [239, 68, 68],
};

const MARGIN = 18;
const FOOTER_Y_OFFSET = 14;

const DOC_BADGES = {
  diet: { label: 'NUTRITION', color: COLORS.green },
  training: { label: 'TRAINING', color: COLORS.orange },
  student: { label: 'DOSSIER', color: COLORS.primary },
  report: { label: 'RAPPORT', color: COLORS.purple },
};

function pageSize(doc) {
  return {
    width: doc.internal.pageSize.getWidth(),
    height: doc.internal.pageSize.getHeight(),
  };
}

function formatDateFr() {
  return new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function slugify(text, fallback = 'document') {
  return (text || fallback).replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
}

function setColor(doc, color) {
  doc.setTextColor(...color);
}

function setFill(doc, color) {
  doc.setFillColor(...color);
}

function setDraw(doc, color) {
  doc.setDrawColor(...color);
}

function drawBrandBar(doc, { compact = false, docTitle = '' } = {}) {
  const { width, height } = pageSize(doc);
  const barH = compact ? 10 : 16;

  setFill(doc, COLORS.primaryDark);
  doc.rect(0, 0, width, barH, 'F');
  setFill(doc, COLORS.primary);
  doc.rect(0, barH - 2, width, 2, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(compact ? 11 : 16);
  setColor(doc, COLORS.white);
  doc.text('COACH.', MARGIN, compact ? 7 : 11);

  if (compact && docTitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(doc, [191, 219, 254]);
    const truncated = docTitle.length > 52 ? `${docTitle.slice(0, 49)}…` : docTitle;
    doc.text(truncated, MARGIN + 28, 7);
  }

  return barH;
}

function drawTypeBadge(doc, badge, y) {
  if (!badge) return;
  const { width } = pageSize(doc);
  const label = badge.label || '';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  const padX = 4;
  const padY = 2.5;
  const textW = doc.getTextWidth(label);
  const boxW = textW + padX * 2;
  const boxH = 6;
  const x = width - MARGIN - boxW;
  const boxY = y - boxH + 1;

  setFill(doc, badge.color || COLORS.primary);
  doc.roundedRect(x, boxY, boxW, boxH, 1.5, 1.5, 'F');
  setColor(doc, COLORS.white);
  doc.text(label, x + padX, boxY + 4.2);
}

function addDocumentHeader(doc, { title, subtitle, docType, docTitle }) {
  const barH = drawBrandBar(doc);
  let y = barH + 10;

  drawTypeBadge(doc, DOC_BADGES[docType], y - 2);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  setColor(doc, COLORS.dark);
  const titleLines = doc.splitTextToSize(title || 'Document', pageSize(doc).width - MARGIN * 2);
  doc.text(titleLines, MARGIN, y);
  y += titleLines.length * 8 + 2;

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setColor(doc, COLORS.muted);
    const subLines = doc.splitTextToSize(subtitle, pageSize(doc).width - MARGIN * 2 - 50);
    doc.text(subLines, MARGIN, y);
    y += subLines.length * 5 + 2;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setColor(doc, COLORS.mutedLight);
  doc.text(`Généré le ${formatDateFr()}`, pageSize(doc).width - MARGIN, barH + 8, { align: 'right' });

  setDraw(doc, COLORS.border);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y + 2, pageSize(doc).width - MARGIN, y + 2);

  return y + 10;
}

function addContinuationHeader(doc, docTitle) {
  const barH = drawBrandBar(doc, { compact: true, docTitle });
  return barH + 8;
}

function ensureSpace(doc, y, needed, docTitle = '') {
  const { height } = pageSize(doc);
  if (y + needed <= height - FOOTER_Y_OFFSET) return y;
  doc.addPage();
  return addContinuationHeader(doc, docTitle);
}

function addSectionTitle(doc, y, title, accent = COLORS.primary) {
  y = ensureSpace(doc, y, 16);
  setFill(doc, accent);
  doc.rect(MARGIN, y - 4, 3, 10, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  setColor(doc, COLORS.dark);
  doc.text(title, MARGIN + 7, y + 2);

  return y + 10;
}

function drawStatCards(doc, y, cards, docTitle = '') {
  if (!cards.length) return y;
  const { width } = pageSize(doc);
  const gap = 5;
  const cols = Math.min(cards.length, 4);
  const cardW = (width - MARGIN * 2 - gap * (cols - 1)) / cols;
  const cardH = 30;
  const accentH = 4;
  const pad = 3;
  y = ensureSpace(doc, y, cardH + 6, docTitle);

  cards.forEach((card, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = MARGIN + col * (cardW + gap);
    const cardY = y + row * (cardH + gap);
    const centerX = x + cardW / 2;
    const innerW = cardW - pad * 2;

    setFill(doc, COLORS.white);
    setDraw(doc, COLORS.border);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'FD');

    setFill(doc, card.color || COLORS.primary);
    doc.rect(x, cardY, cardW, accentH, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    setColor(doc, COLORS.muted);
    const labelLines = doc.splitTextToSize(card.label.toUpperCase(), innerW);
    let cursorY = cardY + accentH + 5;
    labelLines.forEach((line) => {
      doc.text(line, centerX, cursorY, { align: 'center' });
      cursorY += 3.4;
    });

    cursorY += 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    setColor(doc, COLORS.dark);
    doc.text(String(card.value), centerX, cursorY, { align: 'center' });

    if (card.unit) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      setColor(doc, COLORS.muted);
      doc.text(card.unit, centerX, cursorY + 5, { align: 'center' });
    }
  });

  const rows = Math.ceil(cards.length / cols);
  return y + rows * (cardH + gap) + 8;
}

function tableTheme(accent = COLORS.primary) {
  return {
    theme: 'plain',
    styles: {
      fontSize: 9,
      cellPadding: { top: 4, right: 5, bottom: 4, left: 5 },
      lineColor: COLORS.border,
      lineWidth: 0.2,
      textColor: COLORS.dark,
      valign: 'middle',
    },
    headStyles: {
      fillColor: accent,
      textColor: COLORS.white,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 5, right: 5, bottom: 5, left: 5 },
    },
    alternateRowStyles: { fillColor: COLORS.surface },
    margin: { left: MARGIN, right: MARGIN },
  };
}

function renderTable(doc, options) {
  autoTable(doc, options);
  return doc.lastAutoTable.finalY + 8;
}

function addCallout(doc, y, { title, text, variant = 'ai', docTitle = '' }) {
  if (!text) return y;

  const { width } = pageSize(doc);
  const textWidth = width - MARGIN * 2 - 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  const lines = doc.splitTextToSize(text, textWidth);
  const boxHeight = lines.length * 4.8 + (title ? 16 : 10);

  y = ensureSpace(doc, y, boxHeight + 8, docTitle);

  const palette = variant === 'ai'
    ? { fill: COLORS.purpleLight, accent: COLORS.purple, label: 'Analyse IA' }
    : { fill: COLORS.surface, accent: COLORS.primary, label: title || 'Note' };

  setFill(doc, palette.fill);
  setDraw(doc, palette.accent);
  doc.setLineWidth(0.35);
  doc.roundedRect(MARGIN, y, width - MARGIN * 2, boxHeight, 3, 3, 'FD');

  setFill(doc, palette.accent);
  doc.rect(MARGIN, y, 3, boxHeight, 'F');

  let textY = y + 8;
  if (title || variant === 'ai') {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setColor(doc, palette.accent);
    doc.text(variant === 'ai' ? palette.label : title, MARGIN + 8, textY);
    textY += 7;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  setColor(doc, COLORS.dark);
  doc.text(lines, MARGIN + 8, textY);

  return y + boxHeight + 10;
}

function addMealBlock(doc, y, meal, index, docTitle = '') {
  const { width } = pageSize(doc);
  y = ensureSpace(doc, y, 24, docTitle);

  setFill(doc, COLORS.greenLight);
  setDraw(doc, [187, 247, 208]);
  doc.setLineWidth(0.25);
  doc.roundedRect(MARGIN, y, width - MARGIN * 2, 9, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setColor(doc, COLORS.green);
  doc.text(meal.name || `Repas ${index + 1}`, MARGIN + 5, y + 6);
  y += 13;

  if (meal.items?.length > 0) {
    y = renderTable(doc, {
      startY: y,
      body: meal.items.map((item, i) => [`${i + 1}.`, item]),
      ...tableTheme(COLORS.green),
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', textColor: COLORS.muted, fontStyle: 'bold' },
        1: { cellWidth: 'auto' },
      },
      styles: { ...tableTheme(COLORS.green).styles, fontSize: 9.5 },
      head: [],
      showHead: 'never',
    });
  }

  return y + 2;
}

const LIFT_LABELS_PDF = { squat: 'Squat', bench: 'Bench', deadlift: 'Deadlift', other: 'Autre' };

function formatExerciseRow(ex, planDiscipline) {
  const mod = resolveExerciseModality(ex, planDiscipline);
  const prescription = formatExercisePrescription(ex, planDiscipline, {}) || '—';

  if (mod === 'strength') {
    return [
      ex.name || '—',
      ex.sets ?? '—',
      ex.reps ?? '—',
      ex.rest ?? '—',
      ex.lift_key ? (LIFT_LABELS_PDF[ex.lift_key] || ex.lift_key) : '—',
      prescription,
    ];
  }
  if (mod === 'running') {
    return [
      ex.name || '—',
      ex.sets ?? '—',
      ex.distance_km ? `${ex.distance_km} km` : '—',
      ex.zone ? ex.zone.toUpperCase() : '—',
      ex.target_pace || '—',
      prescription,
    ];
  }
  if (mod === 'cycling') {
    return [
      ex.name || '—',
      ex.sets ?? '—',
      ex.duration_min ? `${ex.duration_min} min` : '—',
      ex.target_pct_ftp ? `${ex.target_pct_ftp}% FTP` : '—',
      ex.target_watts ? `${ex.target_watts} W` : '—',
      prescription,
    ];
  }
  if (mod === 'swimming') {
    return [
      ex.name || '—',
      ex.sets ?? '—',
      ex.distance_m ? `${ex.distance_m} m` : '—',
      ex.target_pace_100m || '—',
      ex.css_offset_sec ? `CSS${Number(ex.css_offset_sec) >= 0 ? '+' : ''}${ex.css_offset_sec}s` : '—',
      prescription,
    ];
  }
  return [
    ex.name || '—',
    ex.duration_min ? `${ex.duration_min} min` : '—',
    ex.rpe ? `RPE ${ex.rpe}` : '—',
    ex.notes || '—',
    prescription,
  ];
}

function tableHeadersForDiscipline(planDiscipline) {
  const d = normalizeDiscipline(planDiscipline);
  if (d === 'strength') {
    return [['Exercice', 'Sér.', 'Reps', 'Repos', 'Lift', 'Prescription']];
  }
  if (d === 'running') {
    return [['Bloc', '×', 'Distance', 'Zone', 'Allure', 'Prescription']];
  }
  if (d === 'cycling') {
    return [['Bloc', '×', 'Durée', '%FTP', 'Watts', 'Prescription']];
  }
  if (d === 'swimming') {
    return [['Série', '×', 'Distance', '/100m', 'CSS+', 'Prescription']];
  }
  if (d === 'hybrid') {
    return [['Bloc', 'Type', 'Détail', 'Cible', 'Prescription']];
  }
  return [['Activité', 'Durée', 'RPE', 'Notes', 'Prescription']];
}

function formatHybridRow(ex, planDiscipline) {
  const mod = resolveExerciseModality(ex, planDiscipline);
  const modLabel = getDiscipline(mod).shortLabel;
  const prescription = formatExercisePrescription(ex, planDiscipline, {}) || '—';
  const detail = formatExerciseRow(ex, mod).slice(1, 4).filter((v) => v !== '—').join(' · ') || '—';
  const target = formatExerciseRow(ex, mod).slice(-2, -1)[0] || '—';
  return [ex.name || '—', modLabel, detail, target, prescription];
}

function addDayBlock(doc, y, day, docTitle = '', planDiscipline = 'strength') {
  y = addSectionTitle(doc, y, day.day || 'Jour', COLORS.orange);

  if (day.exercises?.length > 0) {
    const discipline = normalizeDiscipline(planDiscipline);
    const isHybrid = discipline === 'hybrid';
    const head = tableHeadersForDiscipline(discipline);

    const body = day.exercises.map((ex) => (
      isHybrid
        ? formatHybridRow(ex, discipline)
        : formatExerciseRow(ex, discipline)
    ));

    const colCount = head[0].length;
    const colStyles = { 0: { cellWidth: 'auto', fontStyle: 'bold' } };
    for (let i = 1; i < colCount; i++) {
      colStyles[i] = { cellWidth: i === colCount - 1 ? 36 : 22, halign: 'center' };
    }

    y = renderTable(doc, {
      startY: y,
      head,
      body,
      ...tableTheme(COLORS.orange),
      styles: { ...tableTheme(COLORS.orange).styles, fontSize: 8.5 },
      columnStyles: colStyles,
    });
  }

  return y;
}

function addPlanSummaryCard(doc, y, plan, accent, docTitle = '') {
  const { width } = pageSize(doc);
  const cardH = 16;
  y = ensureSpace(doc, y, cardH + 4, docTitle);

  setFill(doc, COLORS.white);
  setDraw(doc, COLORS.border);
  doc.setLineWidth(0.25);
  doc.roundedRect(MARGIN + 4, y, width - MARGIN * 2 - 4, cardH, 2, 2, 'FD');

  setFill(doc, accent);
  doc.rect(MARGIN + 4, y, 2.5, cardH, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setColor(doc, COLORS.dark);
  doc.text(plan.title || 'Plan', MARGIN + 10, y + 7);

  const meta = [];
  if (plan.calories_target) meta.push(`${plan.calories_target} kcal`);
  if (plan.protein_target) meta.push(`${plan.protein_target} g protéines`);
  if (plan.days?.length) meta.push(`${plan.days.length} jour(s)`);
  if (plan.discipline) {
    const d = getDiscipline(plan.discipline);
    meta.push(d.shortLabel || d.label);
  }

  if (meta.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(doc, COLORS.muted);
    doc.text(meta.join('  ·  '), MARGIN + 10, y + 12.5);
  }

  return y + cardH + 5;
}

function addFooters(doc, subtitle = 'Document généré par COACH.') {
  const { width, height } = pageSize(doc);
  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    setDraw(doc, COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, height - FOOTER_Y_OFFSET, width - MARGIN, height - FOOTER_Y_OFFSET);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    setColor(doc, COLORS.mutedLight);
    doc.text(subtitle, MARGIN, height - 8);
    doc.text(`Page ${i} / ${pageCount}`, width - MARGIN, height - 8, { align: 'right' });
  }
}

export function exportDietPlanPDF(plan, aiAnalysis = null) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const docTitle = plan.title || 'Plan Diététique';
  let y = addDocumentHeader(doc, {
    title: docTitle,
    subtitle: plan.description,
    docType: 'diet',
    docTitle,
  });

  const macroCards = [];
  if (plan.calories_target) macroCards.push({ label: 'Calories', value: plan.calories_target, unit: 'kcal', color: COLORS.primary });
  if (plan.protein_target) macroCards.push({ label: 'Protéines', value: plan.protein_target, unit: 'g', color: COLORS.green });
  if (plan.carbs_target) macroCards.push({ label: 'Glucides', value: plan.carbs_target, unit: 'g', color: COLORS.orange });
  if (plan.fat_target) macroCards.push({ label: 'Lipides', value: plan.fat_target, unit: 'g', color: COLORS.purple });
  if (plan.water_target) macroCards.push({ label: 'Eau', value: plan.water_target, unit: 'L', color: COLORS.primary });
  if (plan.steps_target) macroCards.push({ label: 'Pas', value: plan.steps_target, unit: '/jour', color: COLORS.orange });

  if (macroCards.length > 0) {
    y = addSectionTitle(doc, y, 'Objectifs nutrition & activité', COLORS.green);
    y = drawStatCards(doc, y, macroCards, docTitle);
  }

  if (plan.meals?.length > 0) {
    y = addSectionTitle(doc, y, 'Repas', COLORS.green);
    plan.meals.forEach((meal, i) => {
      y = addMealBlock(doc, y, meal, i, docTitle);
    });
  }

  y = addCallout(doc, y, { text: aiAnalysis, variant: 'ai', docTitle });

  addFooters(doc, 'Plan nutrition — COACH.');
  doc.save(`plan-diet-${slugify(plan.title)}.pdf`);
}

export function exportTrainingPlanPDF(plan, aiAnalysis = null) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const docTitle = plan.title || 'Programme Entraînement';
  let y = addDocumentHeader(doc, {
    title: docTitle,
    subtitle: plan.description,
    docType: 'training',
    docTitle,
  });

  if (plan.days?.length > 0) {
    y = addSectionTitle(doc, y, 'Programme hebdomadaire', COLORS.orange);
    plan.days.forEach((day) => {
      y = addDayBlock(doc, y, day, docTitle, plan.discipline || 'strength');
    });
  }

  y = addCallout(doc, y, { text: aiAnalysis, variant: 'ai', docTitle });

  addFooters(doc, 'Programme training — COACH.');
  doc.save(`programme-${slugify(plan.title, 'training')}.pdf`);
}

export function exportStudentDataPDF({ student, dietPlans = [], trainingPlans = [], progress = [], checkins = [], photos = [] }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const docTitle = `Dossier — ${student?.name || 'Élève'}`;
  let y = addDocumentHeader(doc, {
    title: docTitle,
    subtitle: student?.email || '',
    docType: 'student',
    docTitle,
  });

  y = addSectionTitle(doc, y, 'Informations élève', COLORS.primary);
  y = renderTable(doc, {
    startY: y,
    body: [
      ['Nom', student?.name || '—'],
      ['Email', student?.email || '—'],
      ['Objectif poids', student?.goal_weight ? `${student.goal_weight} kg` : '—'],
      ['Date objectif', student?.goal_date || '—'],
    ],
    ...tableTheme(COLORS.primary),
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 48, fillColor: COLORS.surface },
      1: { cellWidth: 'auto' },
    },
  });

  if (progress.length > 0) {
    y = addSectionTitle(doc, y, 'Progression', COLORS.primary);
    y = ensureSpace(doc, y, 40, docTitle);
    y = renderTable(doc, {
      startY: y,
      head: [['Date', 'Poids (kg)', 'Notes']],
      body: progress.slice(0, 20).map((p) => [p.date || '—', p.weight ?? '—', p.notes || '']),
      ...tableTheme(COLORS.primary),
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 'auto' },
      },
    });
  }

  if (checkins.length > 0) {
    y = addSectionTitle(doc, y, 'Check-ins hebdomadaires', COLORS.purple);
    y = renderTable(doc, {
      startY: y,
      head: [['Semaine', 'Poids', 'Humeur', 'Énergie', 'Adhérence']],
      body: checkins.slice(0, 12).map((c) => [
        c.week_start || '—',
        c.weight ?? '—',
        c.mood ?? '—',
        c.energy ?? '—',
        c.adherence ?? '—',
      ]),
      ...tableTheme(COLORS.purple),
      styles: { ...tableTheme(COLORS.purple).styles, fontSize: 8.5 },
      columnStyles: {
        0: { cellWidth: 34 },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 22, halign: 'center' },
        4: { cellWidth: 24, halign: 'center' },
      },
    });
  }

  if (dietPlans.length > 0) {
    y = addSectionTitle(doc, y, 'Plans nutrition', COLORS.green);
    dietPlans.forEach((plan) => {
      y = addPlanSummaryCard(doc, y, plan, COLORS.green, docTitle);
    });
    y += 4;
  }

  if (trainingPlans.length > 0) {
    y = addSectionTitle(doc, y, 'Plans training', COLORS.orange);
    trainingPlans.forEach((plan) => {
      y = addPlanSummaryCard(doc, y, plan, COLORS.orange, docTitle);
    });
  }

  if (photos.length > 0) {
    y = ensureSpace(doc, y, 12, docTitle);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(doc, COLORS.muted);
    doc.text(`${photos.length} photo(s) de suivi enregistrée(s)`, MARGIN, y);
  }

  addFooters(doc, 'Dossier élève confidentiel — COACH.');
  doc.save(`dossier-${slugify(student?.name, 'eleve')}.pdf`);
}

export function exportMonthlyReportPDF(report) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const studentName = report.student?.name || 'Élève';
  const docTitle = `Rapport Mensuel — ${studentName}`;
  let y = addDocumentHeader(doc, {
    title: docTitle,
    subtitle: report.month_label || report.month,
    docType: 'report',
    docTitle,
  });

  if (report.weight_stats && Object.keys(report.weight_stats).length > 0) {
    const ws = report.weight_stats;
    y = addSectionTitle(doc, y, 'Évolution du poids', COLORS.primary);
    y = drawStatCards(doc, y, [
      { label: 'Début', value: ws.start, unit: 'kg', color: COLORS.primary },
      { label: 'Fin', value: ws.end, unit: 'kg', color: COLORS.green },
      { label: 'Min', value: ws.min, unit: 'kg', color: COLORS.muted },
      { label: 'Variation', value: `${ws.diff > 0 ? '+' : ''}${ws.diff}`, unit: 'kg', color: ws.diff <= 0 ? COLORS.green : COLORS.orange },
    ], docTitle);
  }

  if (report.averages) {
    const av = report.averages;
    const avgCards = [];
    if (av.energy != null) avgCards.push({ label: 'Énergie', value: av.energy, unit: '/10', color: COLORS.orange });
    if (av.sleep != null) avgCards.push({ label: 'Sommeil', value: av.sleep, unit: '/10', color: COLORS.primary });
    if (av.adherence != null) avgCards.push({ label: 'Adhérence', value: av.adherence, unit: '/10', color: COLORS.green });
    if (av.mood != null) avgCards.push({ label: 'Humeur', value: av.mood, unit: '/10', color: COLORS.purple });

    if (avgCards.length > 0) {
      y = addSectionTitle(doc, y, 'Moyennes check-ins', COLORS.purple);
      y = drawStatCards(doc, y, avgCards, docTitle);
    }
  }

  if (report.meal_stats) {
    const ms = report.meal_stats;
    y = addSectionTitle(doc, y, 'Journal alimentaire', COLORS.green);
    y = renderTable(doc, {
      startY: y,
      body: [
        ['Jours enregistrés', `${ms.logged_days} / ${ms.days_in_month}`],
        ['Conformité', `${ms.compliance_pct}%`],
        ['Calories moy./jour', ms.avg_calories != null ? `${ms.avg_calories} kcal` : '—'],
        ['Protéines moy./jour', ms.avg_protein != null ? `${ms.avg_protein} g` : '—'],
      ],
      ...tableTheme(COLORS.green),
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 62, fillColor: COLORS.surface },
        1: { cellWidth: 'auto', fontStyle: 'bold' },
      },
    });
  }

  if (report.training_plans?.length > 0) {
    y = addSectionTitle(doc, y, 'Programmes actifs', COLORS.orange);
    report.training_plans.forEach((plan) => {
      y = addPlanSummaryCard(doc, y, plan, COLORS.orange, docTitle);
    });
    y += 4;
  }

  if (report.workout_stats?.sessions_count > 0) {
    const ws = report.workout_stats;
    y = addSectionTitle(doc, y, 'Séances loguées', COLORS.orange);
    const rows = [['Séances', `${ws.sessions_count}`]];
    if (ws.total_distance_km != null) rows.push(['Distance totale', `${ws.total_distance_km} km`]);
    if (ws.total_volume_kg != null) rows.push(['Volume force', `${ws.total_volume_kg} kg`]);
    if (ws.total_duration_min != null) rows.push(['Durée totale', `${ws.total_duration_min} min`]);
    if (ws.disciplines?.length) {
      rows.push(['Disciplines', ws.disciplines.map((id) => getDiscipline(id).shortLabel).join(', ')]);
    }
    y = renderTable(doc, {
      startY: y,
      body: rows,
      ...tableTheme(COLORS.orange),
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 62, fillColor: COLORS.surface },
        1: { cellWidth: 'auto', fontStyle: 'bold' },
      },
    });
  }

  if (report.coach_comments?.length > 0) {
    y = addSectionTitle(doc, y, 'Commentaires coach', COLORS.purple);
    report.coach_comments.forEach((c) => {
      const label = `Semaine du ${c.week_start}`;
      y = addCallout(doc, y, {
        title: label,
        text: c.coach_comment || '',
        variant: 'comment',
        docTitle,
      });
    });
  }

  addFooters(doc, 'Rapport mensuel — COACH.');
  doc.save(`rapport-${slugify(studentName)}-${slugify(report.month, 'rapport')}.pdf`);
}